import { type FxRateRepository } from "../interfaces";
import { supabase } from "@/integrations/supabase/supabaseClient";
import { type Tables, type TablesInsert } from "@/integrations/supabase/types";
import {
  type CurrencyPair,
  type DateRange,
  type FxRate,
  type ISO4217,
  type RateCriteria,
} from "@/types/fx";

// Helpers
function toISODateOnly(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function mapRowToFxRate(row: Tables<"fx_rates">): FxRate {
  return {
    id: row.id,
    baseCurrency: row.base_currency,
    targetCurrency: row.target_currency,
    // Supabase types indicate number; coerce defensively in case of string from numeric
    rate:
      typeof row.rate === "string"
        ? parseFloat(row.rate as unknown as string)
        : (row.rate as number),
    date: new Date(`${row.rate_date}T00:00:00Z`),
    source: row.source,
    createdAt: row.created_at ? new Date(row.created_at) : undefined,
  };
}

/**
 * Supabase-backed FxRateRepository using typed client against public.fx_rates.
 * Note: RLS currently allows SELECT only; upserts will require privileged context.
 */
export class SupabaseFxRateRepository implements FxRateRepository {
  async getHistoricalRate(
    from: ISO4217,
    to: ISO4217,
    date: Date,
  ): Promise<FxRate | null> {
    const day = toISODateOnly(date);

    // Try exact day first
    {
      const { data, error } = await supabase
        .from("fx_rates")
        .select("*")
        .eq("base_currency", from)
        .eq("target_currency", to)
        .eq("rate_date", day)
        .order("rate_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (data) return mapRowToFxRate(data as Tables<"fx_rates">);
    }

    // Fallback: nearest previous day
    {
      const { data, error } = await supabase
        .from("fx_rates")
        .select("*")
        .eq("base_currency", from)
        .eq("target_currency", to)
        .lte("rate_date", day)
        .order("rate_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (data) return mapRowToFxRate(data as Tables<"fx_rates">);
    }

    return null;
  }

  async getCurrentRate(from: ISO4217, to: ISO4217): Promise<FxRate | null> {
    const { data, error } = await supabase
      .from("fx_rates")
      .select("*")
      .eq("base_currency", from)
      .eq("target_currency", to)
      .order("rate_date", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapRowToFxRate(data as Tables<"fx_rates">);
  }

  async getHistoricalRatesFor(
    pairs: CurrencyPair[],
    dateRange: DateRange,
  ): Promise<FxRate[]> {
    if (pairs.length === 0) return [];
    const start = toISODateOnly(dateRange.start);
    const end = toISODateOnly(dateRange.end);

    const bases = Array.from(new Set(pairs.map((p) => p.base)));
    const targets = Array.from(new Set(pairs.map((p) => p.target)));
    const pairSet = new Set(pairs.map((p) => `${p.base}->${p.target}`));

    // Broader query then filter in app since PostgREST can't IN composite easily
    const { data, error } = await supabase
      .from("fx_rates")
      .select("*")
      .in("base_currency", bases)
      .in("target_currency", targets)
      .gte("rate_date", start)
      .lte("rate_date", end);

    if (error) throw error;
    const rows = (data ?? []).filter((r) =>
      pairSet.has(
        `${(r as Tables<"fx_rates">).base_currency}->${(r as Tables<"fx_rates">).target_currency}`,
      ),
    ) as Tables<"fx_rates">[];

    return rows.map(mapRowToFxRate);
  }

  async upsertRates(rates: FxRate[]): Promise<void> {
    if (rates.length === 0) return;

    const payload: TablesInsert<"fx_rates">[] = rates.map((r) => ({
      base_currency: r.baseCurrency,
      target_currency: r.targetCurrency,
      rate: r.rate,
      rate_date: toISODateOnly(r.date),
      source: r.source,
    }));

    const { error } = await supabase
      .from("fx_rates")
      // onConflict must match the UNIQUE(base_currency, target_currency, rate_date, source)
      .upsert(payload, {
        ignoreDuplicates: false,
        onConflict: "base_currency,target_currency,rate_date,source",
      });

    if (error) throw error;
  }

  async getCachedRates(criteria: RateCriteria): Promise<FxRate[]> {
    let query = supabase.from("fx_rates").select("*");

    if (criteria.baseCurrency) {
      query = query.eq("base_currency", criteria.baseCurrency);
    }
    if (criteria.targetCurrency) {
      query = query.eq("target_currency", criteria.targetCurrency);
    }
    if (criteria.source) {
      query = query.eq("source", criteria.source);
    }
    if (criteria.date) {
      const d = toISODateOnly(criteria.date);
      query = query.eq("rate_date", d);
    } else {
      if (criteria.startDate) {
        query = query.gte("rate_date", toISODateOnly(criteria.startDate));
      }
      if (criteria.endDate) {
        query = query.lte("rate_date", toISODateOnly(criteria.endDate));
      }
    }

    const { data, error } = await query.order("rate_date", {
      ascending: false,
    });

    if (error) throw error;
    return ((data as Tables<"fx_rates">[] | null) ?? []).map(mapRowToFxRate);
  }
}
